// Global WalletConnect connection-approval modal (mobile-web only).
//
// SECURITY GATE: approving a WC proposal exposes the selected public account to
// an external dApp across the requested chains and grants every EIP-155 sign
// method — for a privacy wallet that links the public account to that dApp. So
// it MUST be an explicit user decision. The worker (walletConnectWalletBridge)
// holds the SDK proposal and emits a `proposal` event; useWalletConnect surfaces
// it as `proposal`; this overlay renders the design's approve/reject sheet and
// dispatches the answer. Without an approve, nothing is granted. Closing the
// sheet (✕) counts as reject — never a silent grant.
//
// Mounted once from Router.web (like SignMessageOverlay) so the prompt appears
// regardless of which route the user pasted the wc: URI on. Styling matches the
// Figma design (node 36:3741): centered 480px card, DS theme tokens (light/dark),
// supported chains shown as reusable NetworkIcon logos instead of name chips.
import React from 'react'
import { Pressable, View } from 'react-native'

import { useTranslation } from 'react-i18next'

import CloseIcon from '@common/assets/svg/CloseIcon'
import LinkIcon from '@common/assets/svg/LinkIcon'
import NetworkIcon from '@common/components/NetworkIcon'
import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import ManifestImage from '@web/components/ManifestImage'

import { RADII } from '../tokens'
import useWalletConnect from '../useWalletConnect'

// CAIP chain id ("eip155:369") -> bare chain id ("369") for NetworkIcon.
const chainIdOf = (caip: string): string => String(caip).split(':')[1] || String(caip)

const WalletConnectProposalOverlay = () => {
  const { theme } = useTheme()
  const { t } = useTranslation()
  const { proposal, approveProposal, rejectProposal } = useWalletConnect()

  if (!proposal) return null

  const host = proposal.url.replace(/^https?:\/\//, '') || proposal.name
  const chainIds = Array.from(new Set(proposal.chains.map(chainIdOf)))
  // Cap logos to avoid overflow when a dApp requests many chains; surface the
  // rest as a "+N" chip.
  const MAX_VISIBLE_CHAINS = 10
  const visibleChainIds = chainIds.slice(0, MAX_VISIBLE_CHAINS)
  const extraChainsCount = chainIds.length - visibleChainIds.length

  return (
    <View
      style={
        {
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1100,
          backgroundColor: theme.backdrop as string,
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16
        } as any
      }
    >
      <View
        style={{
          width: 480,
          maxWidth: '90%',
          padding: 24,
          gap: 24,
          borderRadius: RADII.lg,
          borderWidth: 1,
          borderColor: theme.homeCardBorder as string,
          backgroundColor: theme.homeCardBackground as string
        }}
      >
        <View style={{ gap: 16 }}>
          {/* Header — ✕ (reject) right-aligned. */}
          <View style={{ flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Pressable
              onPress={rejectProposal}
              hitSlop={6}
              style={({ hovered }: any) => ({
                width: 36,
                height: 36,
                borderRadius: RADII.pill,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.onboardingIconBackground as string,
                opacity: hovered ? 0.85 : 1
              })}
            >
              <CloseIcon width={15} height={15} color={theme.primaryText as string} />
            </Pressable>
          </View>

          {/* Identity + consent copy + supported chains, centered. */}
          <View style={{ alignItems: 'center', gap: 16 }}>
            <View
              style={{
                width: 64,
                height: 64,
                borderRadius: 18,
                borderWidth: 1,
                borderColor: theme.controlSurfaceBorder as string,
                backgroundColor: theme.controlSurfaceBackground as string,
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}
            >
              {proposal.icon ? (
                <ManifestImage
                  uri={proposal.icon}
                  size={64}
                  fallback={() => (
                    <LinkIcon width={26} height={26} color={theme.primary as string} />
                  )}
                  imageStyle={{ borderRadius: 10 }}
                  containerStyle={{ borderRadius: 10 }}
                />
              ) : (
                <LinkIcon width={26} height={26} color={theme.primary as string} />
              )}
            </View>

            <View style={{ alignSelf: 'stretch', gap: 8 }}>
              <Text
                fontSize={20}
                weight="semiBold"
                style={{ textAlign: 'center', color: theme.primaryText as string }}
              >
                {t('Connect {{name}} to your wallet?', { name: proposal.name || host })}
              </Text>
              <Text
                fontSize={14}
                style={{ textAlign: 'center', color: theme.secondaryText as string }}
              >
                {t(
                  'This app will be able to view your wallet address and request transactions or signatures. You stay in control — every request still needs your approval.'
                )}
              </Text>
            </View>

            {chainIds.length > 0 && (
              <>
                {/* Divider */}
                <View
                  style={{
                    alignSelf: 'stretch',
                    height: 1,
                    backgroundColor: theme.controlSurfaceBorder as string
                  }}
                />
                <View style={{ alignSelf: 'stretch', alignItems: 'center', gap: 12 }}>
                  <Text
                    fontSize={14}
                    weight="semiBold"
                    style={{ textAlign: 'center', color: theme.secondaryText as string }}
                  >
                    {t('Supported networks')}
                  </Text>
                  <View
                    style={{
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 12
                    }}
                  >
                    {visibleChainIds.map((id) => (
                      <View
                        key={id}
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 999,
                          borderWidth: 1,
                          borderColor: theme.controlSurfaceBorder as string,
                          overflow: 'hidden'
                        }}
                      >
                        <NetworkIcon id={id} size={28} />
                      </View>
                    ))}
                    {extraChainsCount > 0 && (
                      <View
                        style={{
                          height: 28,
                          minWidth: 28,
                          paddingHorizontal: 6,
                          borderRadius: 999,
                          borderWidth: 1,
                          borderColor: theme.controlSurfaceBorder as string,
                          backgroundColor: theme.controlSurfaceBackground as string,
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Text
                          fontSize={12}
                          weight="semiBold"
                          style={{ color: theme.secondaryText as string }}
                        >
                          {`+${extraChainsCount}`}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
              </>
            )}
          </View>
        </View>

        {/* Footer: Connect (primary) above Reject (destructive). */}
        <View style={{ gap: 16 }}>
          <Pressable
            onPress={approveProposal}
            style={({ hovered }: any) => ({
              alignSelf: 'stretch',
              paddingVertical: 12,
              paddingHorizontal: 20,
              borderRadius: RADII.sm,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.primary as string,
              opacity: hovered ? 0.9 : 1
            })}
          >
            <Text fontSize={16} weight="semiBold" style={{ color: theme.white as string }}>
              {t('Connect')}
            </Text>
          </Pressable>
          <Pressable
            onPress={rejectProposal}
            style={({ hovered }: any) => ({
              alignSelf: 'stretch',
              paddingVertical: 12,
              paddingHorizontal: 20,
              borderRadius: RADII.sm,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.dangerBackground as string,
              opacity: hovered ? 0.9 : 1
            })}
          >
            <Text
              fontSize={16}
              weight="semiBold"
              style={{ color: theme.errorDecorative as string }}
            >
              {t('Reject')}
            </Text>
          </Pressable>
        </View>
      </View>
    </View>
  )
}

export default React.memo(WalletConnectProposalOverlay)
